import { Link } from 'react-router-dom';
// Hero image served from /public for stable URL + preload support in index.html
import { Building2, FileCheck, ClipboardList, FileText, Hammer, CheckCircle, Landmark, Route, Wrench, ArrowRight, HardHat, Search, Briefcase, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import Seo from '@/components/Seo';
import KeyFiguresSection from '@/components/home/KeyFiguresSection';
import PartnersBand from '@/components/home/PartnersBand';
import LatestNewsBand from '@/components/home/LatestNewsBand';
import LocalAnchorLinks from '@/components/seo/LocalAnchorLinks';
import CompanyProfileCTA from '@/components/CompanyProfileCTA';
import { useInView } from '@/hooks/useInView';
import { getProfileUrl, getProfileDownloadName } from '@/constants/companyProfile';
import { posthog } from '@/lib/posthog';
import { WHATSAPP_LINK, WHATSAPP_NUMBER_DISPLAY } from '@/constants/contact';
import { trackCtaClick } from '@/lib/ctaTracking';

const Index = () => {
  const { t, language } = useLanguage();

  const whyChooseSection = useInView();
  const servicesSection = useInView();
  const methodologySection = useInView();
  const ctaSection = useInView();

  const whyChooseItems = [
    { icon: CheckCircle, titleKey: 'whyChoose.quality.title', descKey: 'whyChoose.quality.desc' },
    { icon: ClipboardList, titleKey: 'whyChoose.schedule.title', descKey: 'whyChoose.schedule.desc' },
    { icon: HardHat, titleKey: 'whyChoose.safety.title', descKey: 'whyChoose.safety.desc' },
    { icon: FileCheck, titleKey: 'whyChoose.transparency.title', descKey: 'whyChoose.transparency.desc' },
  ];

  const services = [
    { key: 'buildings', icon: Building2 },
    { key: 'civil', icon: Landmark },
    { key: 'vrd', icon: Route },
    { key: 'renovation', icon: Wrench },
    { key: 'earthworks', icon: Search },
    { key: 'hydraulics', icon: Briefcase },
  ];

  const methodologySteps = [
    { key: 'step1', icon: ClipboardList },
    { key: 'step2', icon: FileText },
    { key: 'step3', icon: Hammer },
    { key: 'step4', icon: CheckCircle },
  ];

  return (
    <Layout>
      <Seo
        title={t('seo.home.title')}
        description={t('seo.home.desc')}
      />

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative min-h-[90vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        <picture>
          <source
            type="image/avif"
            srcSet="/hero-construction-kinshasa-new-480.avif 480w, /hero-construction-kinshasa-new-768.avif 768w, /hero-construction-kinshasa-new-1280.avif 1280w, /hero-construction-kinshasa-new-1920.avif 1920w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="/hero-construction-kinshasa-new-480.webp 480w, /hero-construction-kinshasa-new-768.webp 768w, /hero-construction-kinshasa-new-1280.webp 1280w, /hero-construction-kinshasa-new-1920.webp 1920w"
            sizes="100vw"
          />
          <img
            src="/hero-construction-kinshasa-new-1280.jpg"
            srcSet="/hero-construction-kinshasa-new-480.jpg 480w, /hero-construction-kinshasa-new-768.jpg 768w, /hero-construction-kinshasa-new-1280.jpg 1280w, /hero-construction-kinshasa-new-1920.jpg 1920w"
            sizes="100vw"
            alt={t('hero.imageAlt')}
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(215,78%,8%,0.88)] via-[hsl(215,78%,10%,0.82)] to-[hsl(215,78%,8%,0.90)]" />

        <div className="relative z-10 container mx-auto px-4 text-center py-16">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-5 animate-fade-in">
              {t('hero.title')}
            </h1>

            <p className="text-xl sm:text-2xl md:text-3xl font-heading font-semibold text-accent mb-6 animate-fade-in animation-delay-100">
              {t('hero.subtitle')}
            </p>

            <p className="text-base sm:text-lg text-primary-foreground/80 max-w-2xl mx-auto mb-8 animate-fade-in animation-delay-200 leading-relaxed">
              {t('hero.intro')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 animate-fade-in animation-delay-300">
              <Button
                size="lg"
                asChild
                className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark font-semibold text-base sm:text-lg px-8 py-6 shadow-lg group"
              >
                <Link
                  to="/quote-request"
                  onClick={() => {
                    try { posthog.capture?.('cta_quote_clicked', { location: 'home_hero' }); } catch { /* ignore */ }
                  }}
                >
                  {t('hero.cta.quote')}
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="border-2 border-primary-foreground text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary active:bg-primary-foreground/90 active:text-primary focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary font-semibold text-base sm:text-lg px-8 py-6"
              >
                <a href={getProfileUrl(language)} download={getProfileDownloadName(language)}>
                  {t('hero.cta.profile')}
                </a>
              </Button>
            </div>

            {/* CTA WhatsApp discret */}
            <div className="mb-8 animate-fade-in animation-delay-300">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCtaClick('whatsapp', 'home_hero', { path: '/', language })}
                aria-label={`WhatsApp ${WHATSAPP_NUMBER_DISPLAY}`}
                className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-4 py-2 text-sm font-medium text-primary-foreground/90 transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {language === 'fr' ? 'Écrire sur WhatsApp' : 'Chat on WhatsApp'}
              </a>
            </div>

            {/* Trust line */}
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-primary-foreground/70 animate-fade-in animation-delay-400">
              {(t('hero.trustLine')).split('•').map((item, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <span className="hidden sm:inline text-accent">•</span>}
                  <CheckCircle className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                  {item.trim()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ WHY CHOOSE MTB BTP ═══════════════════ */}
      <section
        ref={whyChooseSection.ref}
        className="py-20 md:py-24 bg-secondary"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-14 ${whyChooseSection.isInView ? 'animate-fade-in' : 'opacity-0'}`}>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('whyChoose.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('whyChoose.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whyChooseItems.map((item, index) => (
              <div
                key={index}
                className={`bg-card rounded-lg p-6 border border-border shadow-sm hover:shadow-md transition-shadow ${whyChooseSection.isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <item.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-heading font-bold text-base text-foreground mb-2">
                  {t(item.titleKey)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ SERVICES ═══════════════════ */}
      <section
        ref={servicesSection.ref}
        className="py-20 md:py-24 bg-background"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-14 ${servicesSection.isInView ? 'animate-fade-in' : 'opacity-0'}`}>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('home.services.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-10">
            {services.map((service, index) => (
              <div
                key={index}
                className={`group bg-card rounded-lg p-6 border border-border shadow-sm hover:shadow-md transition-all ${servicesSection.isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${(index + 1) * 80}ms` }}
              >
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4 group-hover:bg-accent/15 transition-colors">
                  <service.icon className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                </div>
                <h3 className="font-heading font-bold text-base text-foreground mb-2">
                  {t(`home.svc.${service.key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`home.svc.${service.key}.desc`)}
                </p>
              </div>
            ))}
          </div>

          <div className={`text-center ${servicesSection.isInView ? 'animate-fade-in animation-delay-500' : 'opacity-0'}`}>
            <Link to="/services">
              <Button className="bg-primary text-primary-foreground hover:bg-mtb-blue-dark font-semibold px-8 py-5 group">
                {t('services.cta.all')}
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════ METHODOLOGY ═══════════════════ */}
      <section
        ref={methodologySection.ref}
        className="py-20 md:py-24 bg-secondary"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-14 ${methodologySection.isInView ? 'animate-fade-in' : 'opacity-0'}`}>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('home.methodology.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('home.methodology.subtitle')}
            </p>
          </div>

          {/* Desktop: horizontal timeline */}
          <div className="hidden md:block max-w-5xl mx-auto">
            <div className="relative">
              {/* Connector line */}
              <div className="absolute top-8 left-[10%] right-[10%] h-0.5 bg-border" />
              <div className="grid grid-cols-4 gap-6">
                {methodologySteps.map((step, index) => (
                  <div
                    key={index}
                    className={`text-center ${methodologySection.isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                    style={{ animationDelay: `${(index + 1) * 120}ms` }}
                  >
                    <div className="relative z-10 w-16 h-16 bg-accent text-accent-foreground rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                      <span className="font-heading font-bold text-xl">{index + 1}</span>
                    </div>
                    <div className="p-3 bg-accent/10 rounded-lg w-fit mx-auto mb-3">
                      <step.icon className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="font-heading font-bold text-base text-foreground mb-2">
                      {t(`home.method.${step.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(`home.method.${step.key}.desc`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="md:hidden max-w-md mx-auto">
            <div className="relative border-l-2 border-accent/30 pl-8 space-y-10">
              {methodologySteps.map((step, index) => (
                <div
                  key={index}
                  className={`relative ${methodologySection.isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${(index + 1) * 100}ms` }}
                >
                  <div className="absolute -left-[2.55rem] top-0 w-10 h-10 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-heading font-bold text-sm shadow-md">
                    {index + 1}
                  </div>
                  <h3 className="font-heading font-bold text-base text-foreground mb-1">
                    {t(`home.method.${step.key}.title`)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(`home.method.${step.key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ RÉFÉRENCES VÉRIFIÉES ═══════════════════ */}
      <KeyFiguresSection />
      <PartnersBand />
      <LatestNewsBand />

      {/* ═══════════════════ DOCUMENTS ═══════════════════ */}
      <CompanyProfileCTA />

      {/* ═══════════════════ CTA SECTION ═══════════════════ */}
      <section
        ref={ctaSection.ref}
        className="py-20 md:py-24 bg-primary relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-[hsl(215,80%,7%,0.5)] to-transparent" />
        </div>
        <div className="container mx-auto px-4 text-center relative">
          <div className={`max-w-3xl mx-auto ${ctaSection.isInView ? 'animate-fade-in' : 'opacity-0'}`}>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t('tenders.title')}
            </h2>
            <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8">
              {t('tenders.intro')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/tenders">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-mtb-gold-dark font-semibold px-8 py-6 w-full sm:w-auto group"
                >
                  {t('tenders.cta.partnership')}
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-primary-foreground bg-transparent text-primary-foreground font-semibold px-8 py-6 hover:bg-primary-foreground hover:text-primary active:bg-primary-foreground/90 active:text-primary focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                >
                  {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <LocalAnchorLinks
        keys={[
          'construction-kinshasa',
          'genie-civil-rdc',
          'travaux-publics-congo',
          'references-kinshasa',
          'recrutement',
          'internship',
        ]}
      />
    </Layout>

  );
};

export default Index;
